/* eslint-disable react-native/no-inline-styles */
/**
 * Profile Screen
 * 
 * User profile edit screen
 * - Allows editing name and last_name
 * - Form validation with Yup
 * - Updates Redux customer state on save
 * - Keyboard-aware scrolling
 * - Shows success toast on update
 * 
 * @component
 * @param {Object} navigation - React Navigation object
 */
import React, { useEffect, useState } from 'react';
import { InteractionManager, KeyboardAvoidingView, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Text, TextInput } from 'react-native-paper';
import { Formik } from 'formik/dist';
import * as Yup from 'yup';
import useKeyboardVisibility from '../useKeyboardVisibility';
import { theme } from '../theme/theme';
import AnimatedView from '../components/AnimatedView';
import { errorHandler } from '../services/Utils';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoader, showLoader, showToast } from '../store/info.slice';
import { doPut } from '../services/http';
import { updateCustomer } from '../store/customer.slice';

const InputRoundness = 32;

// Yup validation schema
const SignUpSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    last_name: Yup.string(),
});

function Profile({ navigation }) {
    const dispatch = useDispatch();
    const [interactionCompleted, setIntersectionCompleted] = useState(false);
    const customer = useSelector((state) => state.customer?.customer);
    const info = useSelector((state) => state.info?.info);
    const settings = info?.settings;
    const isKeyboardVisible = useKeyboardVisibility();

    useEffect(() => {
        const task = InteractionManager.runAfterInteractions(() => {
            setIntersectionCompleted(true);
        });
        return () => {
            task.cancel();
        };
    }, []);

    /**
     * Handles profile update form submission
     * @param {Object} params - Formik params
     * @param {Object} params.values - Form values (name, last_name)
     * @param {Function} params.resetForm - Formik reset function
     * @param {Function} params.setSubmitting - Formik setSubmitting function
     */
    const onSubmitHandler = ({ values, resetForm, setSubmitting }) => {
        dispatch(showLoader());
        doPut('/profile', {
            name: values.name,
            last_name: values?.last_name,
            email: customer?.email,
        }).then(response => {
            setSubmitting(false);
            dispatch(hideLoader());
            dispatch(showToast(response.data?.message));
            dispatch(updateCustomer({ key: 'cust_name', value: values?.name }));
            dispatch(updateCustomer({ key: 'last_name', value: values?.last_name }));
            navigation.goBack();
        }).catch(error => {
            errorHandler(error);
            setSubmitting(false);
        });
    };

    return (
        <SafeAreaView edges={['bottom']} style={[styles.container, { backgroundColor: theme.colors.primary }]}>
            {interactionCompleted &&
                <React.Fragment>
                    <AnimatedView>
                        <Formik
                            initialValues={{ name: customer?.cust_name || '', last_name: customer?.cust_last_name || '' }}
                            validationSchema={SignUpSchema}
                            onSubmit={(values, { resetForm, setSubmitting }) => {
                                console.log('Form Data:', values);
                                onSubmitHandler({ values, resetForm, setSubmitting });
                            }}
                        >
                            {({ handleChange, handleBlur, handleSubmit, isSubmitting, values, errors, touched, isValid }) => {
                                return (
                                    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.mainContainer}>
                                        <ScrollView contentContainerStyle={styles.KeyboardAwareScrollView}>
                                            <View>
                                                {/* Name Input */}
                                                <View style={styles.inputContainer}>
                                                    <Text variant="titleMedium">Name</Text>
                                                    <TextInput
                                                        placeholder="Enter your Name"
                                                        mode="outlined"
                                                        theme={{ roundness: InputRoundness }}
                                                        style={styles.input}
                                                        contentStyle={styles.contentStyle}
                                                        value={values?.name || ''}
                                                        onChangeText={handleChange('name')}
                                                        onBlur={handleBlur('name')}
                                                    />
                                                    {touched.name && errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
                                                </View>
                                                {!!customer?.emp_id &&
                                                    <View style={styles.inputContainer}>
                                                        <Text variant="titleMedium">Employee ID</Text>
                                                        <TextInput
                                                            disabled={true}
                                                            readOnly={true}
                                                            placeholder="Enter Employee ID"
                                                            mode="outlined"
                                                            theme={{ roundness: InputRoundness }}
                                                            style={styles.input}
                                                            contentStyle={styles.contentStyle}
                                                            value={customer?.emp_id || ''}
                                                        />
                                                    </View>
                                                }
                                                {/* Mobile Input */}
                                                {!settings?.disable_mobile &&
                                                    <View style={styles.inputContainer}>
                                                        <Text variant="titleMedium">Mobile</Text>
                                                        <TextInput
                                                            placeholder="Enter your mobile"
                                                            mode="outlined"
                                                            theme={{ roundness: InputRoundness }}
                                                            style={styles.input}
                                                            contentStyle={styles.contentStyle}
                                                            // keyboardType="tel"
                                                            value={customer?.mobile || ''}
                                                            disabled={true}
                                                        />
                                                        {touched.mobile && errors.mobile && <Text style={styles.errorText}>{errors.mobile}</Text>}
                                                    </View>
                                                }
                                                {/* Email Input */}
                                                {!settings?.disable_email &&
                                                    <View style={styles.inputContainer}>
                                                        <Text variant="titleMedium">Email</Text>
                                                        <TextInput
                                                            placeholder="Enter your email"
                                                            mode="outlined"
                                                            theme={{ roundness: InputRoundness }}
                                                            style={styles.input}
                                                            contentStyle={styles.contentStyle}
                                                            // keyboardType="email-address"
                                                            value={customer?.email || ''}
                                                            disabled={true}
                                                        />
                                                        {touched.email && errors.email && <Text style={styles.errorText}>{errors.email}</Text>}
                                                    </View>
                                                }
                                            </View>
                                        </ScrollView>
                                        {!isKeyboardVisible && (
                                            <AnimatedView style={{paddingBottom: 16}}>
                                                <Button
                                                    theme={{ roundness: 36 }}
                                                    mode="contained"
                                                    labelStyle={styles.labelStyle}
                                                    onPress={handleSubmit}
                                                    disabled={isSubmitting} // Disable button if form is invalid
                                                >
                                                    Update Profile
                                                </Button>
                                            </AnimatedView>
                                        )}
                                    </KeyboardAvoidingView>
                                );
                            }}
                        </Formik>
                    </AnimatedView>
                </React.Fragment>
            }
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    mainContainer: {
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 16,
        margin: 8,
    },
    KeyboardAwareScrollView: {
        flexGrow: 1,
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 8,
    },
    input: {
        fontSize: 14,
        backgroundColor: '#f6f8fa',
        marginVertical: 4,
        width: '100%',
    },
    contentStyle: {
        paddingLeft: 16,
    },
    inputContainer: {
        marginVertical: 8,
    },
    labelStyle: {
        fontSize: 16,
        letterSpacing: 2,
        paddingVertical: 4,
    },
    signUpText: {
        textDecorationLine: 'underline',
        marginLeft: 4,
    },
    errorText: {
        color: 'red',
        fontSize: 12,
        marginTop: 4,
    },
    passwordStrengthContainer: {
        marginTop: 8,
        height: 4,
        backgroundColor: '#e0e0e0',
        borderRadius: 5,
    },
    passwordStrengthBar: {
        height: '100%',
        borderRadius: 5,
    },
});

export default Profile;
